/**
 * Strips appearance utility classes that conflict with cn-* shadcn presets.
 * Only layout utilities survive (sizing, positioning, flex/grid, margin, visibility).
 *
 * The regex + allowlist live in `./preset-conflict-patterns.ts` so that the
 * skill-side validator (`validate.cjs` R9) and this runtime stay in lockstep.
 *
 * Two modes:
 *   - default (17 components — badge/breadcrumb/button/card/checkbox/
 *     collapsible/data-table/date-picker/dialog/empty/month-picker/radio/
 *     select/sidebar/switch/tab-trigger/textarea): blocklist — strip
 *     appearance tokens, keep semantic text colors and non-matching tokens.
 *   - 'input': allowlist — only layout/spacing tokens (w-full, max-w-*, flex-1,
 *     mt-*, mb-*) survive. Appearance, sizing (h-*), focus, placeholder,
 *     shadow, etc. all get stripped because shadcn <input> is fully
 *     renderer-managed and any extra token fights the preset.
 */

import {
  APPEARANCE_UTILITY_REGEX,
  ALLOWED_TEXT_COLOR_REGEX,
  INPUT_PREFIX_STRIP_REGEX,
  INPUT_ALLOWED_TOKEN_REGEX,
} from "./preset-conflict-patterns"

export type PresetComponentKind = 'input'

export function stripPresetConflicts(
  className: string | undefined,
  componentKind?: PresetComponentKind,
): string {
  if (!className) return ''

  // Dynamic expressions can't be statically tokenized — pass them through
  // unchanged so bind/ternary className work. Validator applies the same skip.
  const trimmed = className.trim()
  if (trimmed.startsWith('#{')) return className

  const tokens = className.split(/\s+/).filter(Boolean)

  if (componentKind === 'input') {
    // Allowlist: keep only tokens in the input contract.
    return tokens
      .filter(token => {
        const bare = token.replace(INPUT_PREFIX_STRIP_REGEX, '')
        return INPUT_ALLOWED_TOKEN_REGEX.test(bare)
      })
      .join(' ')
  }

  // Default blocklist — button/card/select/badge.
  return tokens
    .filter(token => {
      if (!APPEARANCE_UTILITY_REGEX.test(token)) return true  // not prohibited -> keep
      if (ALLOWED_TEXT_COLOR_REGEX.test(token)) return true   // text-color exception -> keep
      return false                                            // prohibited -> strip
    })
    .join(' ')
}
